import React from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

interface IRevenueReportDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    hideDialog: () => void;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem' };

// Static help text - set once in WHEN-NEW-FORM-INSTANCE on the Oracle Forms side (:gl_detail.dscr),
// not derived from the selected Report Type - always shows all three WIP modes at once.
const DSCR_TEXT =
`WIP NOT APPLIED
All invoices treated as is, WIP is not in effect.

WIP APPLIED
Lists Non-WIP project invoices and WIP project invoices with WIP in effect.

WIP ONLY
Only WIP projects invoices with WIP in effect.`;

const todayIsoDate = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${mm}-${dd}`;
};

// usp_Revenue parses beg_date/end_date with try_convert(datetime2, @beg_date, 101) - MM/DD/YYYY -
// while the native <input type="date"> fields this dialog uses always emit ISO (YYYY-MM-DD).
const isoDateToMMDDYYYY = (isoDate: string) => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
};

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const RevenueReportDialog = (props: IRevenueReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { post, response } = useFetch();

    const OKClicked = async () => {
        const data = getValues();
        const reportOptions = data?.reportOptions;

        try {
            await post('/v1.0/reports/revenue', {
                reportName: reportOptions?.reportName,
                type: reportOptions?.type,
                beginDate: isoDateToMMDDYYYY(reportOptions?.beginDate),
                endDate: isoDateToMMDDYYYY(reportOptions?.endDate)
            });

            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, 'Revenue.pdf');
            }
        } catch (e) {
            console.log('Unable to generate Revenue report.', e);
        }

        props.handleReport?.(reportOptions);
        props.hideDialog();
    };

    return (
        <Dialog title="Revenue Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFSelect
                        label="Group By"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.reportName"
                        options={[
                            { value: 'Revenue', label: 'State, Client, Project' },
                            { value: 'RevenueManager', label: 'Manager, Client, Project' },
                            { value: 'RevenueClient', label: 'Client, Project' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="Revenue"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.type"
                        options={[
                            { value: 'STD', label: 'WIP Not Applied' },
                            { value: 'WIP', label: 'WIP Applied' },
                            { value: 'WIPONLY', label: 'WIP Only' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="STD"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.beginDate"
                        type="date"
                        register={register}
                        errors={errors}
                        defaultVal={todayIsoDate()}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        defaultVal={todayIsoDate()}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div
                style={{
                    margin: '1rem 5% 0',
                    padding: '0.75rem',
                    backgroundColor: '#fff9c4',
                    border: '1px solid #d4c400',
                    whiteSpace: 'pre-line',
                    fontSize: '0.85rem'
                }}
            >
                {DSCR_TEXT}
            </div>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default RevenueReportDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/RevenueReportDialog.tsx